import React, { useCallback, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { DragIndicator, RestartAlt } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import featureFlag from 'application/common/featureFlag';

// ── Types ──

interface TabOrderShape {
  aiInspection: string[];
  standard: string[];
}

type TabOrderMode = 'aiInspection' | 'standard';

interface TabOrderConfigProps {
  tabOrder: TabOrderShape | null;
  onChange: (newTabOrder: TabOrderShape) => void;
}

// ── Defaults ──

const DEFAULT_TAB_ORDER: TabOrderShape = {
  aiInspection: [
    'AI',
    'Photos',
    'Claim',
    'Contracts',
    'Notes',
    'Lead',
    'Door data',
    'Appointments',
    'Reviews',
  ],
  standard: [
    'Door data',
    'Notes',
    'Status',
    'Appointments',
    'Lead',
    'Contracts',
    'Photos',
    'Claim',
    'Reviews',
  ],
};

// ── Feature flag checks per tab, matching mobile logic exactly ──

function isTabVisibleForMode(label: string, mode: TabOrderMode): boolean {
  const appSettings = (global as any).appSettings;

  switch (label) {
    case 'AI':
      return !!featureFlag('notesToHank');
    case 'Contracts':
      return !!(
        appSettings?.features?.deal?.signOnDevice ||
        appSettings?.features?.deal?.signUsingEmail
      );
    case 'Reviews':
      return !!featureFlag('reviewRequest');
    case 'Appointments':
      return !!appSettings?.features?.schedule?.enabled;
    case 'Status':
      return mode === 'standard';
  }

  return true;
}

// ── Sortable tab chip ──

interface SortableTabChipProps {
  id: string;
  label: string;
  index: number;
}

function SortableTabChip({ id, label, index }: SortableTabChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 1,
        borderRadius: '10px',
        backgroundColor: isDragging
          ? '#FFF3E0'
          : theme.custom?.background2 || '#f5f5f5',
        border: `1px solid ${theme.custom?.stroke || '#e0e0e0'}`,
        cursor: 'grab',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        '&:hover': {
          borderColor: theme.custom?.primary || '#FF6A00',
        },
        '&:active': { cursor: 'grabbing' },
      })}
      {...attributes}
      {...listeners}
    >
      <DragIndicator
        sx={(theme) => ({
          fontSize: 16,
          color: theme.custom?.textLight || '#999',
        })}
      />
      <Typography
        sx={(theme) => ({
          fontSize: 13,
          color: theme.custom?.textLight || '#666',
          mr: 0.25,
        })}
      >
        {index + 1}.
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: 13,
          fontWeight: 500,
          color: theme.custom?.text || '#333',
        })}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ── Horizontal sortable list ──

interface TabOrderListProps {
  subtitle: string;
  tabs: string[];
  onReorder: (tabs: string[]) => void;
  onReset: () => void;
}

function TabOrderList({
  subtitle,
  tabs,
  onReorder,
  onReset,
}: TabOrderListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const items = useMemo(
    () => tabs.map((label, i) => ({ id: `${i}-${label}`, label })),
    [tabs]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove([...tabs], oldIndex, newIndex));
      }
    },
    [items, tabs, onReorder]
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          sx={(theme) => ({
            fontSize: 12,
            color: theme.custom?.textLight || '#888',
          })}
        >
          {subtitle}
        </Typography>
        <IconButton
          size="small"
          onClick={onReset}
          title="Reset to default order"
          sx={(theme) => ({
            color: theme.custom?.textLight || '#999',
            '&:hover': { color: theme.custom?.primary || '#FF6A00' },
          })}
        >
          <RestartAlt sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={horizontalListSortingStrategy}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {items.map((item, index) => (
              <SortableTabChip
                key={item.id}
                id={item.id}
                label={item.label}
                index={index}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      <Typography
        sx={(theme) => ({
          fontSize: 11,
          color: theme.custom?.textLight || '#aaa',
          mt: 1.5,
          fontStyle: 'italic',
        })}
      >
        {/* Door data and Status only appear on the map pin view. */}
      </Typography>
    </Box>
  );
}

// ── Main component ──

export default function TabOrderConfig({
  tabOrder,
  onChange,
}: TabOrderConfigProps) {
  // Mode must match mobile's featureFlag("AIInspection") check exactly
  // so the Configurator reads/writes the same key the mobile app uses.
  const isAI = !!featureFlag('AIInspection');
  const activeMode: TabOrderMode = isAI ? 'aiInspection' : 'standard';

  const config = useMemo(() => {
    const merged: TabOrderShape = JSON.parse(JSON.stringify(DEFAULT_TAB_ORDER));
    if (tabOrder?.aiInspection?.length) {
      merged.aiInspection = tabOrder.aiInspection;
      // Append any new default tabs that aren't in the saved config
      for (const tab of DEFAULT_TAB_ORDER.aiInspection) {
        if (!merged.aiInspection.includes(tab)) {
          merged.aiInspection.push(tab);
        }
      }
    }
    if (tabOrder?.standard?.length) {
      merged.standard = tabOrder.standard;
      for (const tab of DEFAULT_TAB_ORDER.standard) {
        if (!merged.standard.includes(tab)) {
          merged.standard.push(tab);
        }
      }
    }
    return merged;
  }, [tabOrder]);

  // Filter to only tabs visible for the active mode
  const visibleTabs = useMemo(
    () =>
      config[activeMode].filter((label) =>
        isTabVisibleForMode(label, activeMode)
      ),
    [config, activeMode]
  );

  const handleReorder = useCallback(
    (newTabs: string[]) => {
      const updated: TabOrderShape = JSON.parse(JSON.stringify(config));
      updated[activeMode] = newTabs;
      onChange(updated);
    },
    [config, activeMode, onChange]
  );

  const handleReset = useCallback(() => {
    const updated: TabOrderShape = JSON.parse(JSON.stringify(config));
    updated[activeMode] = [...DEFAULT_TAB_ORDER[activeMode]].filter((label) =>
      isTabVisibleForMode(label, activeMode)
    );
    onChange(updated);
  }, [config, activeMode, onChange]);

  return (
    <Box>
      <Typography
        sx={(theme) => ({
          fontWeight: 600,
          fontSize: 16,
          color: theme.custom?.text,
          mb: 0.5,
        })}
      >
        Tab Order
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: 13,
          color: theme.custom?.textLight || '#888',
          mb: 2,
        })}
      >
        Drag to reorder tabs on the pin drop and deal info screens. Users will
        need to log out and back in for changes to take effect.
      </Typography>

      <TabOrderList
        subtitle={''}
        tabs={visibleTabs}
        onReorder={handleReorder}
        onReset={handleReset}
      />
    </Box>
  );
}

export { DEFAULT_TAB_ORDER };
export type { TabOrderShape };
